import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/image3.jpg";
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  FileTextOutlined,
  SearchOutlined,
  CheckCircleFilled,
  SafetyCertificateOutlined,
  AuditOutlined,
  ThunderboltOutlined,
  ArrowRightOutlined,
  BankOutlined,
  LockOutlined,
  SyncOutlined,
} from "@ant-design/icons";

const steps = [
  {
    icon: <FileTextOutlined />,
    label: "Select 'Pay Fees & Taxes via IRAS PRN'",
    sub: "App, USSD *123# or web portal",
  },
  {
    icon: <SearchOutlined />,
    label: "Enter the PRN issued by the municipality",
    sub: "Payment Reference Number from authority",
  },
  {
    icon: <SyncOutlined />,
    label: "Fee details fetched from government system",
    sub: "Live pull — no manual entry errors",
  },
  {
    icon: <CheckCircleFilled />,
    label: "Confirm the payable amount",
    sub: "Verified directly from the source",
  },
  {
    icon: <LockOutlined />,
    label: "Complete payment securely",
    sub: "PIN, biometrics, or mobile money",
  },
];

const benefits = [
  {
    icon: <BankOutlined />,
    title: "Direct Municipal Integration",
    body: "Connects directly to municipal revenue systems — no middlemen, no manual reconciliation.",
  },
  {
    icon: <ThunderboltOutlined />,
    title: "Instant Dual Confirmation",
    body: "Both the payer and the authority receive confirmation the moment payment clears.",
  },
  {
    icon: <AuditOutlined />,
    title: "Zero Revenue Leakage",
    body: "Eliminates manual reconciliation errors and reduces revenue leakage for municipalities.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Secure & Auditable",
    body: "Every transaction is encrypted, timestamped, and available for instant audit or dispute resolution.",
  },
];

export default function PayFeesTaxes() {
  return (
    <Wrapper>
      {/* ══ TOP: Two-panel split ══ */}
      <SplitRow>
        {/* Left dark panel — headline + PRN card mockup */}
        <DarkPane
          data-aos="fade-right"
          data-aos-duration="750"
          data-aos-delay="100"
        >
          <DarkPaneContent>
            <PaneEyebrow>Pay Fees &amp; Taxes</PaneEyebrow>
            <PaneHeadline>
              Municipal &amp;
              <br />
              Government <AccentGold>Fees,</AccentGold>
              <br />
              Digitised.
            </PaneHeadline>
            <PaneSubline>
              OnePayX supports municipal and local government fees using IRAS
              PRNs — instant, traceable, and connected directly to revenue
              systems.
            </PaneSubline>

            {/* PRN lookup card */}
            <PRNCard
              data-aos="fade-up"
              data-aos-duration="650"
              data-aos-delay="350"
            >
              <PRNCardHeader>
                <PRNHeaderIcon>
                  <FileTextOutlined />
                </PRNHeaderIcon>
                <PRNHeaderText>
                  <PRNHeaderTitle>IRAS PRN Lookup</PRNHeaderTitle>
                  <PRNHeaderSub>Municipal Revenue Authority</PRNHeaderSub>
                </PRNHeaderText>
                <PRNLiveBadge>
                  <PRNLiveDot />
                  Live
                </PRNLiveBadge>
              </PRNCardHeader>

              <PRNDivider />

              <PRNInputRow>
                <PRNInputLabel>Payment Reference Number</PRNInputLabel>
                <PRNInputDisplay>PRN-2024-MKL-007821</PRNInputDisplay>
              </PRNInputRow>

              <PRNResultGrid>
                <PRNResultItem>
                  <PRNResultLabel>Fee Type</PRNResultLabel>
                  <PRNResultValue>Business Licence</PRNResultValue>
                </PRNResultItem>
                <PRNResultItem>
                  <PRNResultLabel>Authority</PRNResultLabel>
                  <PRNResultValue>KCCA</PRNResultValue>
                </PRNResultItem>
                <PRNResultItem>
                  <PRNResultLabel>Amount Due</PRNResultLabel>
                  <PRNResultValue $highlight>UGX 250,000</PRNResultValue>
                </PRNResultItem>
                <PRNResultItem>
                  <PRNResultLabel>Due Date</PRNResultLabel>
                  <PRNResultValue $warn>31 Mar 2026</PRNResultValue>
                </PRNResultItem>
              </PRNResultGrid>

              <PRNPayButton>
                Pay Now &nbsp;
                <ArrowRightOutlined />
              </PRNPayButton>
            </PRNCard>
          </DarkPaneContent>

          {/* Background decorative */}
          <DarkPaneGlow />
        </DarkPane>

        {/* Right light panel — image + stat pills */}
        <LightPane
          data-aos="fade-left"
          data-aos-duration="750"
          data-aos-delay="200"
        >
          <LightPaneImageWrap>
            <LightPaneImage src={image} alt="Pay Fees & Taxes" />
            <LightPaneGrad />
          </LightPaneImageWrap>

          <StatStrip>
            <StatItem
              data-aos="fade-up"
              data-aos-delay="400"
              data-aos-duration="500"
            >
              <StatVal>100%</StatVal>
              <StatLabel>Authority-verified</StatLabel>
            </StatItem>
            <StatDiv />
            <StatItem
              data-aos="fade-up"
              data-aos-delay="480"
              data-aos-duration="500"
            >
              <StatVal>0</StatVal>
              <StatLabel>Manual entry errors</StatLabel>
            </StatItem>
            <StatDiv />
            <StatItem
              data-aos="fade-up"
              data-aos-delay="560"
              data-aos-duration="500"
            >
              <StatVal>&lt;5s</StatVal>
              <StatLabel>PRN fetch time</StatLabel>
            </StatItem>
          </StatStrip>
        </LightPane>
      </SplitRow>

      {/* ══ STEPS ══ */}
      <StepsSection className="container">
        <StepsLeft
          data-aos="fade-right"
          data-aos-duration="650"
          data-aos-delay="100"
        >
          <StepsSectionLabel>How It Works</StepsSectionLabel>
          <StepsSectionTitle>
            Pay any government fee
            <br />
            in <GoldSpan>5 steps</GoldSpan>
          </StepsSectionTitle>
          <StepsSectionSub>
            From PRN entry to confirmed receipt — the entire flow takes under 60
            seconds.
          </StepsSectionSub>
        </StepsLeft>

        <StepsRight>
          {steps.map((s, i) => (
            <StepRow
              key={i}
              data-aos="fade-left"
              data-aos-delay={180 + i * 85}
              data-aos-duration="580"
            >
              <StepConnectorCol>
                <StepBubble>{i + 1}</StepBubble>
                {i < steps.length - 1 && <StepConnLine />}
              </StepConnectorCol>
              <StepContent>
                <StepIconChip>{s.icon}</StepIconChip>
                <StepText>
                  <StepLabel>{s.label}</StepLabel>
                  <StepSub>{s.sub}</StepSub>
                </StepText>
              </StepContent>
            </StepRow>
          ))}
        </StepsRight>
      </StepsSection>

      {/* ══ BENEFITS — horizontal card row ══ */}
      <BenefitsSection>
        <BenefitsInner className="container">
          <BenefitsEyebrow
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="100"
          >
            Why It Matters
          </BenefitsEyebrow>
          <BenefitsRow>
            {benefits.map((b, i) => (
              <BenefitCard
                key={i}
                data-aos="fade-up"
                data-aos-delay={160 + i * 90}
                data-aos-duration="600"
              >
                <BenefitIconWrap>{b.icon}</BenefitIconWrap>
                <BenefitTitle>{b.title}</BenefitTitle>
                <BenefitBody>{b.body}</BenefitBody>
                <BenefitAccentLine />
              </BenefitCard>
            ))}
          </BenefitsRow>
        </BenefitsInner>
      </BenefitsSection>
    </Wrapper>
  );
}

/* ── Animations ── */
const pulseLive = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(1.4); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #ffffff;
  overflow: hidden;
`;

/* ── Split row ── */
const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 600px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* Dark pane */
const DarkPane = styled.div`
  background: linear-gradient(145deg, #07142e 0%, #0e2458 55%, #091a40 100%);
  padding: 60px 48px 60px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-start;

  @media (max-width: 600px) {
    padding: 48px 24px;
  }
`;

const DarkPaneContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const DarkPaneGlow = styled.div`
  position: absolute;
  top: -80px;
  right: -80px;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle, ${blueColor}30 0%, transparent 70%);
  pointer-events: none;
  z-index: 1;
`;

const PaneEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${goldColor};
  margin-bottom: 18px;
`;

const PaneHeadline = styled.h2`
  font-size: 40px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1.5px;
  line-height: 1.1;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const AccentGold = styled.span`
  color: ${goldColor};
`;

const PaneSubline = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.52);
  line-height: 1.7;
  margin: 0 0 28px;
`;

/* PRN Card */
const PRNCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 18px;
  padding: 20px 22px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
`;

const PRNCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const PRNHeaderIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: ${blueColor};
  color: #fff;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PRNHeaderText = styled.div`
  flex: 1;
`;

const PRNHeaderTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const PRNHeaderSub = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
`;

const PRNLiveBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  color: #22c55e;
  background: #22c55e12;
  border-radius: 6px;
  padding: 3px 9px;
`;

const PRNLiveDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${pulseLive} 1.8s ease-in-out infinite;
`;

const PRNDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin-bottom: 14px;
`;

const PRNInputRow = styled.div`
  margin-bottom: 14px;
`;

const PRNInputLabel = styled.div`
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 4px;
`;

const PRNInputDisplay = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${blueColor};
  font-family: monospace;
  background: ${blueColor}08;
  border: 1px solid ${blueColor}20;
  border-radius: 8px;
  padding: 8px 12px;
  letter-spacing: 0.5px;
`;

const PRNResultGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  margin-bottom: 16px;
`;

const PRNResultItem = styled.div``;

const PRNResultLabel = styled.div`
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 2px;
`;

const PRNResultValue = styled.div<{ $highlight?: boolean; $warn?: boolean }>`
  font-size: 13.5px;
  font-weight: 700;
  color: ${(p) => (p.$highlight ? blueColor : p.$warn ? "#f59e0b" : "#0f172a")};
`;

const PRNPayButton = styled.button`
  width: 100%;
  background: ${blueColor};
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition:
    background 0.22s,
    gap 0.2s;

  &:hover {
    background: #1455a0;
    gap: 10px;
  }
`;

/* Light pane */
const LightPane = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const LightPaneImageWrap = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const LightPaneImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  min-height: 300px;
  transition: transform 0.7s ease;

  ${LightPaneImageWrap}:hover & {
    transform: scale(1.04);
  }
`;

const LightPaneGrad = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 50%,
    rgba(248, 250, 252, 0.9) 100%
  );
`;

const StatStrip = styled.div`
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-top: 1px solid #e8f0f8;
  padding: 20px 28px;
  gap: 0;
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
`;

const StatVal = styled.div`
  font-size: 26px;
  font-weight: 900;
  color: ${blueColor};
  letter-spacing: -1px;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatDiv = styled.div`
  width: 1px;
  height: 36px;
  background: #e2e8f0;
`;

/* ── Steps ── */
const StepsSection = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 64px;
  align-items: flex-start;
  padding: 72px 20px 64px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const StepsLeft = styled.div``;

const StepsSectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 10px;
`;

const StepsSectionTitle = styled.h3`
  font-size: 28px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -0.8px;
  line-height: 1.2;
  margin: 0 0 14px;
`;

const GoldSpan = styled.span`
  color: ${goldColor};
`;

const StepsSectionSub = styled.p`
  font-size: 14px;
  color: #64748b;
  line-height: 1.65;
  margin: 0;
`;

const StepsRight = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
`;

const StepConnectorCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 36px;
  flex-shrink: 0;
`;

const StepBubble = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${blueColor};
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px ${blueColor}40;
`;

const StepConnLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 20px;
  background: linear-gradient(to bottom, ${blueColor}60, ${blueColor}10);
  margin: 4px 0;
`;

const StepContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0 0 20px 12px;
  flex: 1;
`;

const StepIconChip = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${blueColor}10;
  color: ${blueColor};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0;
`;

const StepText = styled.div``;

const StepLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  margin-bottom: 3px;
`;

const StepSub = styled.div`
  font-size: 12px;
  color: #94a3b8;
`;

/* ── Benefits ── */
const BenefitsSection = styled.div`
  background: #f8fafc;
  border-top: 1px solid #e8f0f8;
  padding: 60px 0 72px;
`;

const BenefitsInner = styled.div`
  padding: 0 20px;
`;

const BenefitsEyebrow = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 28px;
`;

const BenefitsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-radius: 18px;
  padding: 24px 20px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.24s ease;
  cursor: default;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px ${blueColor}12;
    border-color: ${blueColor}30;
  }
`;

const BenefitAccentLine = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, ${blueColor} 0%, ${goldColor} 100%);
  opacity: 0;
  transition: opacity 0.24s;

  ${BenefitCard}:hover & {
    opacity: 1;
  }
`;

const BenefitIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: ${blueColor}10;
  color: ${blueColor};
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  transition: background 0.22s;

  ${BenefitCard}:hover & {
    background: ${blueColor}1e;
  }
`;

const BenefitTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const BenefitBody = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
`;

// import React from "react";
// import PageHeader from "../PageHeader";
// import ProductSection from "./ProductSection";
// import taxesImg from "../../../../../assets/img/paytax.jpg";

// export default function FeesTaxes() {
//   return (
//     <>
//       <PageHeader
//         title="Pay Fees & Taxes"
//         description="OnePayX supports municipal and local government fees using IRAS PRNs."
//         backgroundImage={taxesImg}
//         height="65vh"
//       />

//       <ProductSection
//         title="Municipal & Local Government Fees"
//         subtitle="Digitised, Secure, and Traceable"
//         image={taxesImg}
//         imagePosition="right"
//         steps={[
//           "Select 'Pay Fees & Taxes Using IRAS PRN'",
//           "Enter the PRN issued by the municipality",
//           "OnePayX fetches the fee details from the local government system",
//           "Confirm the payable amount",
//           "Complete payment securely",
//         ]}
//         highlights={[
//           "Direct integration with municipal revenue systems",
//           "Instant confirmation to both payer and authority",
//           "Reduced revenue leakage and manual reconciliation",
//           "Secure, traceable, and auditable transactions",
//         ]}
//       />
//     </>
//   );
// }
